// Tercera columna del explorer: el pago elegido — su detalle, qué le hizo al crédito y el contexto
// del caso. El efecto sobre el crédito es el bloque que le dice al cobrador si sacó al cliente de mora.
import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { CircleAlert, FileText, Zap } from 'lucide-react';
import { Button as UiButton } from '@/components/ui/button';
import { Badge, Button, Modal } from '@/kit';
import { formatTimelineDateMx } from '@/lib/dateHelpers';
import { IdText } from '@/shared/components/IdText';
import { plural } from '@/shared/lib/format/plural';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import {
    CONTACT_HISTORY_LIMIT,
    NO_CONTACT_HISTORY_TEXT,
    NO_PROMISE_TEXT,
    formatPaymentAmount,
    formatPaymentDateTime,
    formatPaymentDay,
} from '../logic';
import type { CollectorPaymentRow } from '../types';
import { PaymentChannelChip } from './PaymentsTable';

const SECTION = 'mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

/** Los dos avisos del mock: no hay backend detrás de ninguno de los dos botones. */
const RECEIPT_NOTICE = {
    title: 'Comprobante',
    body: 'Vista del comprobante generado. Descargable en PDF.',
};
const INVESTIGATION_NOTICE = {
    title: 'Abrir investigación',
    body: 'Solo si el cliente reporta problema con este pago.',
};

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="grid grid-cols-[100px_1fr] gap-1.5 py-[3px] text-[11.5px]">
            <span className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </span>
            <span>{value}</span>
        </div>
    );
}

function PromiseValue({ row }: { row: CollectorPaymentRow }) {
    if (row.promise === null) return <>{NO_PROMISE_TEXT}</>;
    return (
        <>
            <Badge variant="success" size="xs">
                Cumple&nbsp;<IdText value={row.promise.promiseId} />
            </Badge>{' '}
            · {formatPaymentAmount(row.promise.amount)} para{' '}
            {formatPaymentDay(row.promise.dueDay)}
        </>
    );
}

function ContactHistory({ row }: { row: CollectorPaymentRow }) {
    if (row.contactHistory.length === 0) {
        return <div className="text-[11px] italic text-muted-foreground">{NO_CONTACT_HISTORY_TEXT}</div>;
    }
    return (
        <div className="text-[11px]">
            {row.contactHistory.slice(0, CONTACT_HISTORY_LIMIT).map((entry) => (
                <div key={entry.at} className="border-b border-border/60 py-1">
                    <div className="text-[10px] text-muted-foreground">
                        {formatTimelineDateMx(entry.at)} · {entry.channel} ·{' '}
                        {entry.outcome.replace('_', ' ')}
                    </div>
                    <div className="text-[11px]">{entry.note === '' ? '—' : entry.note}</div>
                </div>
            ))}
        </div>
    );
}

export function PaymentDetail({ row }: { row: CollectorPaymentRow | null }) {
    const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);

    if (row === null) {
        return (
            <MasterDetailEmpty
                title="Elige un pago"
                sub="La ficha muestra por dónde entró, contra qué compromiso y qué le hizo al crédito."
            />
        );
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex-none border-b border-border px-3.5 py-3">
                <div className="mb-1 flex items-start justify-between gap-2">
                    <span className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Pago recibido
                    </span>
                    <Badge variant="success" size="xs">
                        Folio {row.paymentId.replace('PG-', '')}
                    </Badge>
                </div>
                <IdText
                    value={row.paymentId}
                    className="block text-[15px] font-semibold leading-tight"
                />
                <div className="mt-1 text-[12px]">{row.customerName}</div>
                <div className="text-[10.5px] text-muted-foreground">
                    <IdText value={row.creditId} /> ·{' '}
                    {row.arrearsDaysBefore === 0
                        ? 'al día'
                        : `${String(row.arrearsDaysBefore)}d mora antes`}
                </div>
            </div>

            <div className="flex flex-none flex-wrap gap-1.5 px-3.5 py-2">
                {/* `asChild` para que el botón primario sea el propio enlace del router: el kit
                    no lo expone, así que sale del primitivo. */}
                <UiButton asChild size="sm" className="h-[26px] px-2 text-[11px]">
                    <Link to="/collections/$section" params={{ section: 'agenda' }}>
                        <Zap className="size-2.5" aria-hidden />
                        Abrir caso en Agenda
                    </Link>
                </UiButton>
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-[26px] px-2 text-[11px]"
                    onClick={() => {
                        setNotice(RECEIPT_NOTICE);
                    }}
                >
                    <FileText className="size-2.5" aria-hidden />
                    Comprobante
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-[26px] px-2 text-[11px]"
                    onClick={() => {
                        setNotice(INVESTIGATION_NOTICE);
                    }}
                >
                    <CircleAlert className="size-2.5" aria-hidden />
                    Investigar
                </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3 text-[11.5px]">
                <div className={SECTION}>Detalle</div>
                <InfoRow label="Fecha" value={formatPaymentDateTime(row.receivedAt)} />
                <InfoRow
                    label="Monto"
                    value={
                        <span className="font-semibold tabular-nums">
                            {formatPaymentAmount(row.amount)}
                        </span>
                    }
                />
                <InfoRow label="Canal" value={<PaymentChannelChip row={row} />} />
                <InfoRow label="Compromiso" value={<PromiseValue row={row} />} />

                <div className={`${SECTION} mt-3`}>Efecto sobre el crédito</div>
                <InfoRow label="DPI antes" value={plural(row.arrearsDaysBefore, 'día', 'días')} />
                <InfoRow label="DPI después" value={plural(row.arrearsDaysAfter, 'día', 'días')} />
                <InfoRow
                    label="Cuota aplicada"
                    value={`${formatPaymentAmount(row.appliedInstallment)} aplicada`}
                />

                <div className={`${SECTION} mt-3`}>Contexto</div>
                <ContactHistory row={row} />
            </div>

            <Modal
                isOpen={notice !== null}
                onClose={() => {
                    setNotice(null);
                }}
                title={notice?.title}
                size="sm"
            >
                <div className="text-[13px] leading-relaxed">{notice?.body}</div>
            </Modal>
        </div>
    );
}
